import React, { useEffect, useState, useRef } from "react";
import BlockKitStore from "../stores/BlockKitStore";
import { inject, observer } from "mobx-react";
import { TemplateModel } from "../controllers/StudioAdminController";
import { AllCommunityModule, ICellRendererParams, ModuleRegistry } from "ag-grid-community";
import { Badge, Button, Container, Form, Modal, Stack } from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import { getLocaleString } from "../utils/DateHelper";
import { TemplateTypes } from "../constants/TemplateTypes";
import { useTranslation } from "react-i18next";

ModuleRegistry.registerModules([AllCommunityModule]);

interface Props {
    blockKitStore?: BlockKitStore;
}

const TemplatePage: React.FC<Props> = inject("blockKitStore")(observer(({blockKitStore}) => {
    const [baseUrl, setBaseUrl] = useState<string>();
    const [stagingTemplates, setStagingTemplates] = useState<TemplateModel[]>();
    const [publishTemplates, setPublishTemplates] = useState<TemplateModel[]>();
    const [selectedTemplate, setSelectedTemplate] = useState<TemplateModel>();
    const [isOpenEditModal, setIsOpenEditModal] = useState<boolean>();
    const [editName, setEditName] = useState<string>("");
    const [editType, setEditType] = useState<string>("");
    const [editPriority, setEditPriority] = useState<number | null>(3);
    const gridRef = useRef<AgGridReact<TemplateModel>>(null);
    const { t } = useTranslation();

    const fetchTemplatesAsync = (async () => {
        const stagingRes = await blockKitStore?.studioController.getTemplatesStagingAsync();
        if (stagingRes) {
            setBaseUrl(stagingRes.baseUrl);
            setStagingTemplates(stagingRes.templates);
        }

        const publishRes = await blockKitStore?.studioController.getTemplatesAsync();
        if (publishRes) {
            setPublishTemplates(publishRes.templates);
        }
    });

    const ImageCell = (props: ICellRendererParams) => {
        const imgSrc = `${baseUrl}${props.value}`;
        return props.value ? (
        <a href={imgSrc} target="_blank" rel="noopener noreferrer">
            <img
            src={imgSrc}
            alt="thumbnail"
            style={{ width: "100%", height: "100%", objectFit: 'contain' }}
            />
        </a>
        ) : null;
    };

    const EditButtonCell = (params: ICellRendererParams<TemplateModel>) => {
        return (
            <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                    setSelectedTemplate(params.data);
                    setEditName(params.data?.name ?? "");
                    setEditType(params.data?.templateType ?? "");
                    setEditPriority(params.data?.priority ?? 3);
                    setIsOpenEditModal(true);
                    params.api.deselectAll();
                    params.node.setSelected(true);
                }}>
                {t("button-edit")}
            </Button>
        );
    };

    useEffect(() => {
        fetchTemplatesAsync()
    }, []);

    useEffect(() => {
        gridRef.current?.api?.refreshCells({ force: true });
    }, [blockKitStore?.timeZone]);

    return(
        <Container fluid>
            <Stack className="m-2" direction="horizontal" gap={2}>
              <Button
                variant="primary"
                disabled={!selectedTemplate}
                onClick={async () => {
                  if (!confirm(t("publish-confirmation-message"))) {
                    return;
                  }

                  const response = await blockKitStore?.studioController.publishTemplateStagingAsync(selectedTemplate);
                  if (response?.isSuccess) {
                    await fetchTemplatesAsync();
                    alert(t("publish-success"));
                  } else {
                    alert(`${t("publish-failed")} (${response?.message})`);
                  }
                }}
                >{t("button-publish")}</Button>
              <Button
                variant="outline-danger"
                disabled={!selectedTemplate || !publishTemplates?.some(i => i.templateId === selectedTemplate?.templateId)}
                onClick={async () => {
                  if (!confirm(t("unpublish-confirmation-message"))) {
                    return;
                  }
                  const response = await blockKitStore?.studioController.unpublishTemplateStagingAsync(selectedTemplate);
                  if (response?.isSuccess) {
                    await fetchTemplatesAsync();
                    alert(t("unpublish-success"));
                  } else {
                    alert(`${t("unpublish-failed")} (${response?.message})`);
                  }
                }}
                >{t("button-unpublish")}</Button>
              <Button
                variant="outline-danger"
                disabled={!selectedTemplate || publishTemplates?.some(i => i.templateId === selectedTemplate?.templateId)}
                onClick={async () => {
                  if (!confirm("삭제 하시겠습니까?")) {
                    return;
                  }
                  await blockKitStore?.studioController.deleteTemplateStagingAsync(selectedTemplate!);
                  await fetchTemplatesAsync();

                  alert(t("delete-success"));
                }}
                >{t("button-delete")}</Button>
            </Stack>
            <div style={{ height: "70vh"}}>
                <AgGridReact<TemplateModel>
                    ref={gridRef}
                    getRowId={params => params.data.templateId}
                    rowData={stagingTemplates}
                    enableCellTextSelection={true}
                    columnDefs={[
                        {
                          headerName: "게시",
                          width: 80,
                          cellRenderer: (params: ICellRendererParams) => {
                              return publishTemplates?.some(i => i.templateId === params.data?.templateId)
                              ? <Badge bg="info">게시중</Badge>
                              : null
                          },
                        },
                        {
                            headerName: "수정",
                            width: 80,
                            cellRenderer: EditButtonCell,
                        },
                        {
                            headerName: "미리보기",
                            field: "previewUrl",
                            width: 90,
                            cellRenderer: ImageCell,
                        },
                        {
                            headerName: "아이디",
                            field: "templateId",
                            filter: true
                        },
                        {
                            headerName: "이름",
                            field: "name",
                            filter: true,
                        },
                        {
                            headerName: "우선순위",
                            field: "priority",
                            width: 110,
                            filter: true,
                        },
                        {
                            headerName: "타입",
                            field: "templateType",
                            filter: true,
                        },
                        {
                            headerName: "사이즈",
                            field: "size",
                            filter: true,
                        },
                        {
                            headerName: "방향",
                            field: "direction",
                            filter: true,
                        },
                        {
                            headerName: "생성일자",
                            field: "createDate",
                            filter: "agDateColumnFilter",
                            valueFormatter: params => getLocaleString(new Date(params.data.createDate), blockKitStore?.timeZone),
                        }
                    ]}
                    rowSelection={{
                        mode: "singleRow",
                        enableClickSelection: false,
                    }}
                    onSelectionChanged={(e) => {
                      const selected = e.api.getSelectedRows();
                      if (selected.length > 0) {
                        setSelectedTemplate(selected[0]);
                      } else {
                        setSelectedTemplate(undefined);
                      }
                    }}
                />
            </div>
            <Modal show={isOpenEditModal} onHide={() => setIsOpenEditModal(false)}>
                <Modal.Header closeButton>템플릿 수정</Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-2">
                        <Form.Label>이름</Form.Label>
                        <Form.Control
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label>타입</Form.Label>
                        <Form.Select
                            value={editType}
                            onChange={(e) => setEditType(e.target.value)}
                        >
                            {Object.values(TemplateTypes).map((type) => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>우선순위</Form.Label>
                        <Form.Control
                            type="number"
                            min={1}
                            value={editPriority ?? ""}
                            onChange={(e) => setEditPriority(e.target.value === "" ? null : +e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        disabled={
                            !editName?.trim() ||
                            (editName === selectedTemplate?.name &&
                                editType === selectedTemplate?.templateType &&
                                editPriority === selectedTemplate?.priority)
                        }
                        onClick={async () => {
                            if (!editPriority || !Number.isInteger(editPriority) || editPriority <= 0) {
                                alert("우선순위를 입력해주세요.");
                                return;
                            }
                            await blockKitStore.studioController.putTemplateStagingAsync({
                                ...selectedTemplate,
                                name: editName.trim(),
                                templateType: editType,
                                priority: editPriority
                            });
                            await fetchTemplatesAsync();
                            setIsOpenEditModal(false);
                            alert(t("edit-success"));
                        }}
                    >{t("button-save")}</Button>
                    <Button variant="secondary" onClick={() => setIsOpenEditModal(false)}>{t("button-close")}</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    )
}))

export default TemplatePage;